const supportedChannels = new Set(['email', 'sms']);
const positiveListConditions = new Set(['true_to_any', 'true_to_all', 'any']);

const conditionValue = condition => (
  typeof condition.data === 'string'
    ? condition.data
    : condition.data?.condition
);

const conditionConstraints = condition => ({
  ...(condition.constraints || {}),
  ...(condition.data?.constraints || {}),
});

/**
 * EC-4237: scope a "subscribed to list" condition to the channel the filter
 * already selects, so list membership is correlated with that channel's live
 * subscription instead of the fan's global channel eligibility.
 *
 * Without this, "in list X AND subscribed to email" matches SMS-only members of
 * list X who happen to be globally email-eligible, and the audience count comes
 * out above the number the send actually reaches.
 *
 * Deliberately conservative — it bails out rather than guessing whenever the
 * intent is ambiguous:
 *  - any `or` in the logic (the conditions may not all apply together)
 *  - zero or more than one distinct channel among the `subscribedTo` conditions
 *  - negative/`not` list conditions, where stamping a channel would *widen* an
 *    exclusion rather than narrow an inclusion
 *  - an explicit non-`all` messagingChannel the user already chose
 *
 * Returns the input unchanged (same object identity) when nothing applies, and
 * never mutates the caller's filter.
 *
 * Mirrors audience-manager-3's client/utils/filter/listSubscriptionChannel.js —
 * keep the two in sync.
 */
export function correlateListSubscriptionChannel(filter) {
  if (!filter?.conditions?.length) return filter;
  if (filter.logic?.some(token => String(token).toLowerCase() === 'or')) {
    return filter;
  }

  const channels = filter.conditions
    .filter(condition => condition.name === 'subscribedTo')
    .filter(condition => supportedChannels.has(conditionValue(condition)))
    .map(condition => conditionValue(condition));
  if (new Set(channels).size !== 1) return filter;
  const channel = channels[0];

  let changed = false;
  const conditions = filter.conditions.map((condition) => {
    if (
      condition.name !== 'listSubscriptions'
      || !positiveListConditions.has(conditionValue(condition))
    ) {
      return condition;
    }

    const currentConstraints = conditionConstraints(condition);
    const currentConstraint = currentConstraints.messagingChannel;
    const nestedConstraints
      = condition.data && typeof condition.data === 'object'
        ? condition.data.constraints
        : undefined;

    if (currentConstraint?.condition && currentConstraint.condition !== 'all') {
      // Preserve the user's explicit choice; only hoist a nested constraint so
      // the server sees it in the shape it expects.
      if (!nestedConstraints) return condition;

      changed = true;
      const data = { ...condition.data };
      delete data.constraints;
      return {
        ...condition,
        data,
        constraints: currentConstraints,
      };
    }

    changed = true;
    let data = condition.data;
    if (nestedConstraints) {
      data = { ...condition.data };
      delete data.constraints;
    }

    return {
      ...condition,
      data,
      constraints: {
        ...currentConstraints,
        messagingChannel: {
          condition: channel,
          values: [],
        },
      },
    };
  });

  return changed ? { ...filter, conditions } : filter;
}
